import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Star, FileText, CreditCard, Users, Target, ClipboardList, Calendar,
  Music, Mic, Speaker, Radio, Headphones, Heart, Check, Shield, Zap,
  Award, TrendingUp, Clock, Mail, ArrowRight, Menu, X, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/api/uploads/')) return `${BACKEND_URL}${parsed.pathname}`;
  } catch (e) {}
  return url;
}

// Icon mapping
const iconMap = {
  Star, FileText, CreditCard, Users, Target, ClipboardList, Calendar,
  Music, Mic, Speaker, Radio, Headphones, Heart, Check, Shield, Zap,
  Award, TrendingUp, Clock, Mail
};

export default function PublicHomePage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/frontend-settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default values
  const seo = settings?.seo || {};
  const homePage = settings?.home_page || {};
  const hero = homePage?.hero || {};
  const features = settings?.features || [];

  // Navigation links
  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              {settings?.navbar_logo_url ? (
                <img src={resolveImageUrl(settings.navbar_logo_url)} alt={seo.site_title} className="h-8" />
              ) : (
                <span className="font-bold text-xl text-gray-900">{seo.site_title || 'DJ CRM'}</span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  to={link.href}
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/auth">
                <Button variant="ghost" className="text-gray-700 hover:text-gray-900">
                  Login
                </Button>
              </Link>
              <Link to="/free-trial">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-4">
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  to={link.href}
                  className="block text-gray-600 hover:text-gray-900 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-100 space-y-2">
                <Link to="/auth" className="block">
                  <Button variant="outline" className="w-full">Login</Button>
                </Link>
                <Link to="/free-trial" className="block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 px-4"
        style={{
          background: hero.background_image_url 
            ? `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.95)), url(${resolveImageUrl(hero.background_image_url)})`
            : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {hero.headline || 'Manage Your DJ Business Like a Pro'}
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            {hero.subheadline || 'The all-in-one platform for contracts, invoices, client portals, and more.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={hero.cta_primary_link || '/free-trial'}>
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                {hero.cta_primary_text || 'Start Free Trial'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to={hero.cta_secondary_link || '/auth'}>
              <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 px-8 py-6 text-lg">
                {hero.cta_secondary_text || 'Login'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      {homePage.show_features_section !== false && features.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need to Run Your Business
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Powerful tools designed specifically for DJ professionals
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || Star;
                return (
                  <div 
                    key={feature.id || index}
                    className="p-6 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-100"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                    {feature.screenshot_url && (
                      <img 
                        src={resolveImageUrl(feature.screenshot_url)} 
                        alt={feature.title}
                        className="mt-4 rounded-lg shadow-md w-full"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to="/features">
                <Button variant="outline" size="lg" className="border-gray-300">
                  View All Features
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Streamline Your DJ Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of DJs who trust our platform to manage their bookings and clients.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/free-trial">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Start Your Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <span className="font-bold text-xl text-white">
                {seo.site_title || 'DJ CRM'}
              </span>
              <p className="mt-2 text-sm">
                {settings?.footer_text || '© 2024 DJ CRM. All rights reserved.'}
              </p>
            </div>
            <div className="flex items-center gap-6">
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  to={link.href}
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/auth" className="text-gray-400 hover:text-white text-sm transition-colors">
                Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
